import {FrmVar} from "../../drawfrm/FrmVar";
import {MdFrameStick} from "../../model/frame/MdFrameBlock";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import type {StickResize} from "./StickMaterial";
import {inches} from "../../const";

// smallest a block may be left at when a drag ends. Matches the solver's soft min
// for un-pinned cells; a pinned (dragged) size carries no solver min of its own, so
// the drags enforce it on release: a drag that ends with a block under it reverts.
export const MIN_BLOCK = 3 * inches;

// a void this small or smaller counts as zero: it goes, and its stick lands back on
// the edge. Voids have no other minimum.
export const VOID_ZERO = inches / 8;

// the gap a stick becomes when it is made an air gap (see BlockOps.makeAirGap), and
// the smallest gap allowed (a smaller one has no area to click).
export const DEFAULT_AIR_GAP = inches / 2;
export const MIN_AIR_GAP = inches / 8;

// what a stick drag will do on release, shown in the StatusBar while it drags: "ok"
// when the release keeps the position, "error" when it reverts.
export type DragStatus = {text: string; tone: "error" | "ok"};

export const DRAG_OK: DragStatus = {text: "", tone: "ok"};
export const MIN_NOT_MET: DragStatus = {text: `3" minimum not met`, tone: "error"};
const SNAPS_TO_EDGE: DragStatus = {text: "Release to snap to the edge", tone: "ok"};

// build the drag-to-resize behaviour for an interior mullion. `leading`/`trailing`
// resolve the size FrmVar (width for a vertical mullion, height for a horizontal
// one) of the block on each side — lazily, since the trailing block may not exist
// yet when the stick is constructed. Dragging the stick toward the trailing side
// grows the leading block and shrinks the trailing one by the same amount, so the
// row/column total is preserved; `relayout` re-solves the grid on every move. Past
// a void's zero the drag hands over to the stick on the edge (see snapUnder).
export function makeMullionResize(opts: {
    leading: () => FrmVar;
    trailing: () => FrmVar;
    relayout: () => void;
    // true when the block on that side is a step: left at or under VOID_ZERO it snaps
    // the stick back onto the edge (see snapUnder) rather than an error.
    snapsUnder: (side: "leading" | "trailing") => boolean;
    // snap the stick onto the edge in place of the block on that side. Returns the id
    // of the stick now on the edge, or null when nothing changed.
    snapUnder: (side: "leading" | "trailing") => string | null;
    // show what the release will do (null = nothing to say).
    status: (s: DragStatus | null) => void;
    // called after a side's size is written, with the value it moved from and to.
    onResize?: (side: "leading" | "trailing", from: number, to: number) => void;
    // the ids of every lite and panel cell under MIN_BLOCK, grid-wide. A drag can
    // shrink cells away from the stick (a joined step shares its size), so the release
    // reverts when a cell that met the minimum before the drag is under it now.
    cellsUnderMin?: () => Set<string>;
}): StickResize {
    // a side left too small: a step at or under VOID_ZERO, any other block under
    // MIN_BLOCK.
    const under = (side: "leading" | "trailing") => {
        const size = (side === "leading" ? opts.leading() : opts.trailing()).get();
        return opts.snapsUnder(side) ? size <= VOID_ZERO : size < MIN_BLOCK;
    };
    const underSide = () => under("leading") ? "leading" : under("trailing") ? "trailing" : null;

    // the cells already under MIN_BLOCK when the drag began: they don't count.
    let underBefore = new Set<string>();
    const underElsewhere = () => {
        if(!opts.cellsUnderMin) return false;
        return Array.from(opts.cellsUnderMin()).some(id => !underBefore.has(id));
    };

    let startLeading = 0;
    let startTrailing = 0;
    // whether each side was already pinned before the drag, so end() drops only the
    // pins the drag itself added.
    let leadingWasPinned = false;
    let trailingWasPinned = false;
    // set once move() has actually written a size, so a press-and-release that never
    // moved (a plain select) skips the release + re-render below.
    let moved = false;

    return {
        begin() {
            startLeading = opts.leading().get();
            startTrailing = opts.trailing().get();
            leadingWasPinned = opts.leading().isModified();
            trailingWasPinned = opts.trailing().isModified();
            underBefore = opts.cellsUnderMin?.() ?? new Set();
            moved = false;
        },
        move(delta: number) {
            // past a step's zero the void goes, and the stick's own drag on the edge
            // takes the gesture over, so it grows its cell outward from here. The pins
            // this drag added come off first, as end() would.
            const past = delta > startTrailing ? "trailing" : delta < -startLeading ? "leading" : null;
            if(past && opts.snapsUnder(past)) {
                return {
                    commit: () => {
                        opts.status(null);
                        if(!leadingWasPinned) opts.leading().clearModified();
                        if(!trailingWasPinned) opts.trailing().clearModified();
                        return opts.snapUnder(past);
                    },
                };
            }

            // the drag moves freely; only a negative size is out of reach. The
            // minimum is checked on release (see end).
            const clamped = Math.max(-startLeading, Math.min(startTrailing, delta));
            const curLeading = opts.leading().get();
            const curTrailing = opts.trailing().get();
            // userSet pins both sizes (isModified), so the solver honours the split
            // exactly rather than re-flowing it away. The pin is dropped on end().
            opts.leading().userSet(startLeading + clamped, {debug: "mullion-drag"});
            opts.trailing().userSet(startTrailing - clamped, {debug: "mullion-drag"});
            opts.onResize?.("leading", curLeading, startLeading + clamped);
            opts.onResize?.("trailing", curTrailing, startTrailing - clamped);
            moved = true;
            opts.relayout();

            const under = underSide();
            opts.status(underElsewhere() ? MIN_NOT_MET : !under ? DRAG_OK : opts.snapsUnder(under) ? SNAPS_TO_EDGE : MIN_NOT_MET);
        },
        end() {
            if(!moved) return;

            opts.status(null);

            // a block left too small (see under): a step snaps the stick onto the
            // edge; anything else, here or elsewhere in the grid, goes back to the
            // sizes (and pins) from before the drag.
            const under = underSide();
            const elsewhere = underElsewhere();
            if(under && !elsewhere && opts.snapsUnder(under)) {
                opts.snapUnder(under);
                return;
            }
            if(under || elsewhere) {
                const curLeading = opts.leading().get();
                const curTrailing = opts.trailing().get();
                opts.leading().restore(startLeading, leadingWasPinned);
                opts.trailing().restore(startTrailing, trailingWasPinned);
                opts.onResize?.("leading", curLeading, startLeading);
                opts.onResize?.("trailing", curTrailing, startTrailing);
                opts.relayout();
                return;
            }

            // a drag positions the mullion, it doesn't lock the cells it divides —
            // release the pins move() took so the sizes read (and draw) as
            // solver-owned again. The values stay where the drag left them: they're
            // a consistent solve, so the soft stays reproduce them.
            if(!leadingWasPinned) opts.leading().clearModified();
            if(!trailingWasPinned) opts.trailing().clearModified();
            opts.relayout();
        },
    };
}

// build the "who gives up the space" behaviour for an interior mullion whose
// thickness is edited, so the mullion grows into the cells it separates instead of
// growing the block it sits in. The trailing cell (below in a column, right in a
// row) gives the space up first, falling back to the leading one for whatever the
// trailing one can't give without dropping under MIN_BLOCK.
//
// This only steps in when every neighbour is pinned. An un-pinned neighbour is
// solver-owned, so the layout already reflows the change into it — the same
// reasoning as absorbOuterSizeDelta's solver-free check. Left to itself, a solve
// where both neighbours are pinned is over-determined: their sizes plus the new
// thickness no longer add up to the block, and the solver picks which pin to
// override.
//
// `leading`/`trailing` resolve lazily (the trailing cell doesn't exist yet when the
// stick is built) and return null for a cell that can't give up space at all, i.e.
// one bearing a door. Sizes are written with set(), which moves the value and
// leaves the pin in place, so the neighbour stays user-sized at the size that fits.
export function makeMullionThicknessAbsorb(opts: {
    leading: () => FrmVar | null;
    trailing: () => FrmVar | null;
}) {
    return (delta: number) => {
        if(delta === 0) return;

        const candidates = [opts.trailing(), opts.leading()].filter(v => !!v) as FrmVar[];
        if(candidates.length === 0) return;
        if(candidates.some(v => !v.isModified())) return;

        let remaining = delta;
        for(const v of candidates) {
            const cur = v.get();
            const next = Math.max(MIN_BLOCK, cur - remaining);
            if(next !== cur) v.set(next, {debug: "mullion-thickness-absorb"});

            remaining -= cur - next;
            if(remaining === 0) return;
        }
    };
}

// build the drag-to-resize behaviour for an outline stick. Dragging outward
// grows the edge cell the border abuts — expanding the overall frame — since
// there's no far-side neighbour to give the space back. `sign` maps the drag axis
// onto growth: +1 for the trailing edges (right/bottom, where dragging toward
// positive screen coords is outward), -1 for the leading ones (left/top).
export function makePerimeterResize(opts: {
    cell: FrmVar;
    sign: 1 | -1;
    relayout: () => void;
    // show what the release will do (null = nothing to say).
    status: (s: DragStatus | null) => void;
    // called after the cell's size is written, with the value it moved from and to.
    onResize?: (from: number, to: number) => void;
    // the least the cell may be left at (MIN_BLOCK when omitted): more under a sloped
    // stick, whose short end must keep MIN_BLOCK.
    min?: () => number;
}): StickResize {
    const min = () => opts.min?.() ?? MIN_BLOCK;
    let start = 0;
    // whether the cell was already pinned before the drag, so end() drops only a
    // pin the drag itself added.
    let wasPinned = false;
    // see makeMullionResize: skips the release when the press never became a drag.
    let moved = false;

    return {
        begin() {
            start = opts.cell.get();
            wasPinned = opts.cell.isModified();
            moved = false;
        },
        move(delta: number) {
            // the drag moves freely; the minimum is checked on release (see end).
            const next = Math.max(0, start + delta * opts.sign);
            const cur = opts.cell.get();
            // userSet pins the size (isModified), so the solver honours it exactly —
            // without it the frame's own stay would pull the edge back. Dropped on end().
            opts.cell.userSet(next, {debug: "perimeter-drag"});
            opts.onResize?.(cur, next);
            moved = true;
            opts.relayout();
            opts.status(next < min() ? MIN_NOT_MET : DRAG_OK);
        },
        end() {
            if(!moved) return;
            opts.status(null);

            // the cell left under the minimum: go back to its size (and pin) from
            // before the drag.
            if(opts.cell.get() < min()) {
                const cur = opts.cell.get();
                opts.cell.restore(start, wasPinned);
                opts.onResize?.(cur, start);
                opts.relayout();
                return;
            }

            // see makeMullionResize.end: dragging an edge sizes the cell, it doesn't
            // lock it. The frame kept the size it grew to, so the solve still lands
            // on the dragged value once the pin is gone.
            if(!wasPinned) opts.cell.clearModified();
            opts.relayout();
        },
    };
}

// resolve a stick's thickness FrmVar: an explicit override wins, otherwise the spec's
// jambFace.
export function stickThicknessVar(stick: MdFrameStick, spec: MdFrameSpec): FrmVar {
    if(stick.thickness) return stick.thickness;

    const value = spec.jambFace.get();
    const newVar = new FrmVar(stick.id + ":thick", value)
    stick.thickness = newVar;

    return newVar;
}

export function assertNotNaN(value: any, strict: boolean, err: string): number {
    if(typeof value !== "number") {
        if(strict) throw new Error(err);
        return NaN;
    }

    if(isNaN(value)) {
        if(strict) throw new Error(err);
        return NaN;
    }

    return value;
}

